import { FootprintPolygonsType, StructureImageTransformType } from 'data/types';
import {
  BoundingBox,
  computeFootprintBoundingBox,
  unionBoundingBoxes
} from 'utils/auto-fit-bounding-box';

type ImageDims = { naturalWidth: number; naturalHeight: number };

type ContentBoundsInput = {
  footprintPolygons?: FootprintPolygonsType | null;
  imageTransform?: StructureImageTransformType | null;
  imageDims?: ImageDims | null;
};

const ZOOM_TO_FIT_PADDING = 180;

type MinZoomOptions = {
  padding?: number;
  floor?: number;
  cap?: number;
};

export function imagePixelBounds(
  imageTransform: StructureImageTransformType | null | undefined,
  imageDims: ImageDims | null | undefined
): BoundingBox | null {
  if (!imageTransform || !imageDims) return null;
  if (imageDims.naturalWidth <= 0 || imageDims.naturalHeight <= 0) return null;

  const scale = imageTransform.scale ?? 1;
  const startX = imageTransform.startPositionX ?? 0;
  const startY = imageTransform.startPositionY ?? 0;
  const centerX = startX + imageDims.naturalWidth / 2;
  const centerY = startY + imageDims.naturalHeight / 2;
  const halfWidth = (imageDims.naturalWidth * scale) / 2;
  const halfHeight = (imageDims.naturalHeight * scale) / 2;

  return {
    minX: centerX - halfWidth,
    minY: centerY - halfHeight,
    maxX: centerX + halfWidth,
    maxY: centerY + halfHeight
  };
}

export function computeContentPixelBounds({
  footprintPolygons,
  imageTransform,
  imageDims
}: ContentBoundsInput): BoundingBox | null {
  return unionBoundingBoxes([
    computeFootprintBoundingBox(footprintPolygons ?? null),
    imagePixelBounds(imageTransform, imageDims)
  ]);
}

type ZoomToFitOptions = {
  padding?: number;
  maxZoom?: number;
};

export function computeZoomToFitView(
  bounds: BoundingBox,
  container: { width: number; height: number },
  { padding = ZOOM_TO_FIT_PADDING, maxZoom = 1.5 }: ZoomToFitOptions = {}
): { zoom: number; panX: number; panY: number } {
  const bbWidth = bounds.maxX - bounds.minX;
  const bbHeight = bounds.maxY - bounds.minY;

  const zoom = Math.min(
    bbWidth > 0 ? (container.width - padding * 2) / bbWidth : maxZoom,
    bbHeight > 0 ? (container.height - padding * 2) / bbHeight : maxZoom,
    maxZoom
  );
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;

  return {
    zoom,
    panX: container.width / 2 - centerX * zoom,
    panY: container.height / 2 - centerY * zoom
  };
}

export function computeMinZoom(
  bounds: BoundingBox | null,
  container: { width: number; height: number },
  { padding = ZOOM_TO_FIT_PADDING, floor = 0.25, cap = 1 }: MinZoomOptions = {}
): number {
  if (!bounds) return floor;

  const boxWidth = bounds.maxX - bounds.minX;
  const boxHeight = bounds.maxY - bounds.minY;

  if (
    boxWidth <= 0 ||
    boxHeight <= 0 ||
    container.width <= 0 ||
    container.height <= 0
  ) {
    return floor;
  }

  const fit = Math.min(
    (container.width - padding * 2) / boxWidth,
    (container.height - padding * 2) / boxHeight
  );

  return Math.min(cap, Math.max(floor, fit));
}

const WHEEL_PIXELS_PER_LINE = 16;
const WHEEL_PIXELS_PER_PAGE = 400;
export const WHEEL_ZOOM_SENSITIVITY = 0.0022;

export function normalizeWheelDeltaY(
  deltaY: number,
  deltaMode: number
): number {
  if (deltaMode === 1) {
    return deltaY * WHEEL_PIXELS_PER_LINE;
  }
  if (deltaMode === 2) {
    return deltaY * WHEEL_PIXELS_PER_PAGE;
  }
  return deltaY;
}

export function zoomAfterWheelDelta(
  currentZoom: number,
  deltaY: number,
  deltaMode: number,
  bounds: { min: number; max: number }
): number {
  const pixels = normalizeWheelDeltaY(deltaY, deltaMode);
  const next = currentZoom * Math.exp(-pixels * WHEEL_ZOOM_SENSITIVITY);
  return Math.min(bounds.max, Math.max(bounds.min, next));
}
